import type { GroupPrivate } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { format, parseISO } from 'date-fns';
import { ArrowUpRight, User, Users } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { Link } from 'react-router';

import MeetingCard from '@/components/features/meeting/MeetingCard';
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupButtonMore, AvatarGroupContent, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useElementData } from '@/hooks/element-data';
import { getAvatar } from '@/utils/getAvatar';

/**
 * Props for the GroupCard component.
 * @property {GroupPrivate} group - The group data to display.
 * @property {() => void} [onMouseEnter] - Optional mouse enter callback.
 * @property {() => void} [onFocus] - Optional focus callback.
 */
interface GroupCardProps {
  group: GroupPrivate;
  onMouseEnter?: () => void;
  onFocus?: () => void;
}

export default function GroupCard(groupCardProps: GroupCardProps) {
  const { group, onMouseEnter, onFocus, ...props } = groupCardProps;
  const { id, name, meetings, users, avatarURL } = group;
  const avatarsContainerRef = useRef<HTMLDivElement>(null);
  const [{ width }] = useElementData(avatarsContainerRef);

  const sliceUsers = (users: UserPublic[], containerWidth: number) => {
    let sliceIndex = containerWidth > 0 ? Math.floor(containerWidth / 30) : 4;
    sliceIndex = sliceIndex > 0 ? sliceIndex - 1 : sliceIndex;
    return [users.slice(0, sliceIndex), users.slice(sliceIndex)];
  };

  const avatarGroup = useMemo(() => {
    const [firstGroup, secondGroup] = sliceUsers(users, width);

    return (
      <AvatarGroup>
        <AvatarGroupContent>
          {firstGroup?.map(({ id, name, surname, avatarURL }) => {
            return (
              <Avatar key={id} size={'sm'}>
                <AvatarImage src={getAvatar(avatarURL)} alt={`${name} ${surname}`} />
                <AvatarFallback>
                  <User />
                </AvatarFallback>
              </Avatar>
            );
          })}
          {secondGroup && secondGroup.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <AvatarGroupButtonMore>+{secondGroup?.length}</AvatarGroupButtonMore>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-60 w-full max-w-md">
                {users.map(({ id, name, surname, avatarURL }) => {
                  return (
                    <DropdownMenuLabel key={id} className={'flex items-center gap-2 py-1.5 font-normal'}>
                      <Avatar key={id} size={'sm'}>
                        <AvatarImage src={getAvatar(avatarURL)} alt={`${name} ${surname}`} />
                        <AvatarFallback>
                          <User />
                        </AvatarFallback>
                      </Avatar>
                      <p className="truncate">{name + ' ' + surname}</p>
                    </DropdownMenuLabel>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </AvatarGroupContent>
      </AvatarGroup>
    );
  }, [users, width]);

  return (
    <div
      tabIndex={0}
      className={'transition-outline border-border-primary bg-background outline-focus-ring flex h-fit flex-col rounded-2xl border outline-0 focus-visible:outline-3 disabled:opacity-75'}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      {...props}
    >
      <div className={'border-border-primary flex items-center gap-3 border-b p-4'}>
        <Avatar size={'md'}>
          <AvatarImage src={getAvatar(avatarURL)} alt={'avatar'} />
          <AvatarFallback>
            <Users className={'size-4'} />
          </AvatarFallback>
        </Avatar>
        <h5 className={'text-foreground w-full truncate text-lg font-semibold'}>{name}</h5>
      </div>
      <div className={'flex h-full flex-col gap-3 p-4'}>
        <div className={'flex flex-col gap-2'}>
          <p className={'text-muted-foreground text-xs font-medium'}>Najbliższe zajęcia:</p>
          <div className={'flex min-h-6 flex-wrap gap-1'}>
            {meetings.length ? (
              //limit shown meetings to 2
              meetings.slice(0, 2).map((meeting) => (
                <DropdownMenu key={meeting.id}>
                  <DropdownMenuTrigger>
                    <Badge key={id} variant={'muted'} size={'sm'}>
                      {format(parseISO(meeting.startDate), 'EEE do MMM, HH:mm')}
                    </Badge>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <MeetingCard
                      meeting={{
                        ...meeting,
                        desc: '',
                        isCancelled: false,
                        updatedAt: '',
                        createdAt: '',
                      }}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              ))
            ) : (
              <p className="text-muted-foreground truncate text-xs whitespace-nowrap italic">Brak zajęć w tym tygodniu</p>
            )}
          </div>
        </div>
        <div className={'mt-auto flex items-end justify-between gap-2'}>
          <div ref={avatarsContainerRef} className={'flex h-full w-full items-end'}>
            {users.length > 0 && avatarGroup}
          </div>
          <Link to={`/groups/${id}`} state={group} className={'w-fit'}>
            <Button variant={'outline'}>
              Otwórz <ArrowUpRight className="transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
